import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CollegeMapView from '@/components/college-search/CollegeMapView';
import { CollegeFilters } from '@/hooks/useCollegeSearch';
import { useAuth } from '@/contexts/AuthContext';
import { toggleSaveCollege } from '@/hooks/useCollegeSearch';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

const divisions = ['NCAA D1', 'NCAA D2', 'NCAA D3', 'NAIA', 'NJCAA', 'NCCAA', 'CCCAA', 'Other'];
const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 
  'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const CollegeMapPage: React.FC = () => {
  const { profile } = useAuth();
  const [filters, setFilters] = useState<CollegeFilters>({
    states: [],
    divisions: [],
    conferences: [],
    religiousAffiliations: [],
    tuitionMin: null,
    tuitionMax: null,
    satMin: null,
    satMax: null,
    actMin: null,
    actMax: null,
    gpaMin: null,
    gpaMax: null,
    sports: [],
    searchQuery: ''
  });

  const handleToggleSave = async (collegeId: string) => {
    if (!profile?.id) {
      toast.error("Please log in to save colleges");
      return;
    }
    const result = await toggleSaveCollege(collegeId, profile.id);
    if (result.success) {
      toast.success(result.action === 'added' ? 'College saved!' : 'College removed from saved');
    } else {
      toast.error('Failed to save college');
    }
    return result;
  };

  const handleDivisionChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      divisions: value === 'all' ? [] : [value]
    }));
  };

  const handleStateChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      states: value === 'all' ? [] : [value]
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      searchQuery: e.target.value
    }));
  };

  const clearFilters = () => {
    setFilters({
      states: [],
      divisions: [],
      conferences: [],
      religiousAffiliations: [],
      tuitionMin: null,
      tuitionMax: null,
      satMin: null,
      satMax: null,
      actMin: null,
      actMax: null,
      gpaMin: null,
      gpaMax: null,
      sports: [],
      searchQuery: ''
    });
  };

  const hasActiveFilters = filters.states.length > 0 || filters.divisions.length > 0 || filters.searchQuery;

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white shadow-sm">
        <div className="flex items-center gap-4">
          <Link to="/athlete-dashboard/college-search">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to College Search
            </Button>
          </Link>
          <h1 className="text-xl font-bold text-primary">College Map</h1>
        </div>

        {/* Desktop Filters */}
        <div className="hidden md:flex items-center gap-3">
          <Input
            placeholder="Search colleges..."
            value={filters.searchQuery}
            onChange={handleSearchChange}
            className="w-48"
          />
          
          <Select 
            value={filters.divisions[0] || 'all'} 
            onValueChange={handleDivisionChange}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Division" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Divisions</SelectItem>
              {divisions.map((div) => (
                <SelectItem key={div} value={div}>{div}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={filters.states[0] || 'all'} 
            onValueChange={handleStateChange}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">All States</SelectItem>
              {states.map((state) => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>

        {/* Mobile Filters */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center">
                    {(filters.states.length > 0 ? 1 : 0) + (filters.divisions.length > 0 ? 1 : 0) + (filters.searchQuery ? 1 : 0)}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Filter Colleges</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Search</label>
                  <Input
                    placeholder="Search colleges..."
                    value={filters.searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Division</label>
                  <Select 
                    value={filters.divisions[0] || 'all'} 
                    onValueChange={handleDivisionChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Divisions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Divisions</SelectItem>
                      {divisions.map((div) => (
                        <SelectItem key={div} value={div}>{div}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">State</label>
                  <Select 
                    value={filters.states[0] || 'all'} 
                    onValueChange={handleStateChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="all">All States</SelectItem>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1">
        <CollegeMapView 
          filters={filters} 
          onToggleSave={handleToggleSave}
          fullScreen={true}
        />
      </div>
    </div>
  );
};

export default CollegeMapPage;
